import { Leaf, AlertTriangle, Egg } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

export const BreakfastCard = ({ item, index }) => {
  const brand = useBrand();
  const ageLabels = item.ages
    .map((id) => brand.ageRanges.find((a) => a.id === id)?.label || id)
    .join(" · ");

  return (
    <article
      data-testid={`breakfast-card-${item.id}`}
      className="mbm-card mbm-card-hover flex flex-col overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-creamDeep">
        <img
          src={item.image}
          alt={item.name}
          loading={index < 2 ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
        />
        <span className="absolute left-4 top-4 rounded-full bg-cream/95 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-cocoa backdrop-blur">
          {ageLabels}
        </span>
        <span className="absolute right-4 top-4 rounded-full bg-amber px-3.5 py-1.5 text-[11px] font-extrabold text-cocoa">
          {item.label}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h3 className="font-heading text-lg font-semibold leading-snug">{item.name}</h3>
        {item.tradition && (
          <p className="mt-3 border-l-2 border-rust pl-3.5 text-[13px] italic leading-relaxed text-cocoa/75">
            {item.tradition}
          </p>
        )}
        <p className="mt-3 text-sm leading-relaxed text-cocoa/70">{item.description}</p>

        <dl className="mt-5 space-y-3 border-t border-line pt-5 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-[20px] bg-sageSoft p-4">
              <dt className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-cocoa/70">
                Parent note
              </dt>
              <dd className="mt-1.5 text-[13px] font-medium leading-relaxed text-cocoa/70">{item.parentNote}</dd>
            </div>
            <div className="rounded-[20px] bg-amber/15 p-4">
              <dt className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-coralInk">
                For your child
              </dt>
              <dd className="mt-1.5 text-[13px] font-medium leading-relaxed text-cocoa/75">{item.kidNote}</dd>
            </div>
          </div>

          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
              Ingredients
            </dt>
            <dd className="mt-1.5 flex flex-wrap gap-1.5">
              {item.ingredients.map((ing) => (
                <span
                  key={ing}
                  className="rounded-full bg-cream px-3 py-1 text-[12px] font-semibold text-cocoa/75"
                >
                  {ing}
                </span>
              ))}
            </dd>
          </div>

          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
              Contains
            </dt>
            <dd className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {item.vegetarian ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-sage px-3 py-1 text-[12px] font-bold text-cocoa">
                  <Leaf className="h-3 w-3" aria-hidden="true" /> Vegetarian
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold"
                  style={{ background: "rgba(196,54,56,0.12)", color: "#9E2B2D" }}
                >
                  <Egg className="h-3 w-3" aria-hidden="true" /> Contains egg
                </span>
              )}
              {item.allergens.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold"
                  style={{ background: "rgba(196,54,56,0.12)", color: "#9E2B2D" }}
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> {a}
                </span>
              ))}
            </dd>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Storage
              </dt>
              <dd className="mt-1 text-[13px] leading-relaxed text-cocoa/80">{item.storage}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Reheating
              </dt>
              <dd className="mt-1 text-[13px] leading-relaxed text-cocoa/80">{item.reheating}</dd>
            </div>
          </div>

          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
              Availability
            </dt>
            <dd className="mt-1 font-medium text-coralInk">{item.availability}</dd>
          </div>
        </dl>
      </div>
    </article>
  );
};

export default BreakfastCard;
